import React from 'react';
import { ProgressProps, ProgressTone } from '../../../types/DesignSystem/Progress';
import Typography from '../Typography/Typography';
import { withClassName } from '../cn';

/**
 * Progress (§ 211) — the determinate bar. See the type file for the
 * Spinner/Progress split (indeterminate vs measured).
 *
 * The FILL is the § 206 tone bolds' first consumer: `accent` runs on
 * `accent-bold` (the saturated brand fill, § 61), and the outcome tones on
 * `tone-{success,warning,danger}-bold` — each already measured ≥3:1 against
 * the surface as a state boundary, which is exactly the duty a bar's fill
 * performs against its track.
 */

const TRACK_H: Record<'sm' | 'md', string> = {
  sm: 'tw-h-1',
  md: 'tw-h-2',
};

/* One decision, never stacked — the fieldBorder arrangement, for fills. */
const TONE_FILL: Record<ProgressTone, string> = {
  accent: 'tw-bg-accent-bold',
  success: 'tw-bg-tone-success-bold',
  warning: 'tw-bg-tone-warning-bold',
  danger: 'tw-bg-tone-danger-bold',
};

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(function Progress({
  value,
  label,
  showValue = false,
  size = 'md',
  tone = 'accent',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* Clamped, and NaN-proofed: a fetch that divides by zero must render an empty
     bar, not `width: NaN%` (which the browser silently drops, leaving the last
     rendered width on screen — a bar that LOOKS stuck at its previous value). */
  const clamped = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;

  return (
    <div
      {...rest}
      ref={ref}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      aria-label={label}
      data-testid={dataTestId}
      className={withClassName(`tw-flex tw-items-center tw-gap-2`, className)}
    >
      {/* § 224: forced colors strips both the sunken track and the tone fill —
          the bar simply vanished. The track keeps its extent with a forced border;
          the fill takes the system Highlight, that palette's own "amount" colour. */}
      <div className={`tw-flex-1 ${TRACK_H[size]} tw-bg-surface-sunken tw-rounded-full tw-overflow-hidden forced-colors:tw-border`}>
        <div
          data-testid={dataTestId ? `${dataTestId}-bar` : undefined}
          className={`tw-h-full tw-rounded-full ${TONE_FILL[tone]} forced-colors:tw-bg-[color:Highlight] motion-safe:tw-transition-[width] motion-safe:tw-duration-slow motion-safe:tw-ease-swift`}
          /* Inline, necessarily: a width class cannot be built from a value —
             the scanner only sees literals (the palette-swatch reasoning). */
          style={{ width: `${clamped}%` }}
        />
      </div>
      {showValue && (
        <Typography
          variant="caption-metadata"
          className="tw-text-ink-secondary tw-tabular-nums tw-shrink-0"
        >
          <span data-testid={dataTestId ? `${dataTestId}-value` : undefined}>{Math.round(clamped)}%</span>
        </Typography>
      )}
    </div>
  );
});

export default Progress;
